import React, { useState, useEffect, useRef } from 'react';
import { Upload, Button, message, Card, Space, Table, Timeline, Typography, Progress, Alert, Tag, Divider, Popconfirm, Tooltip, Modal, List, Badge, Select } from 'antd';
import { UploadOutlined, DownloadOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, DeleteOutlined, InfoCircleOutlined, ClockCircleOutlined, FileExcelOutlined, GlobalOutlined, StopOutlined } from '@ant-design/icons';
import { dataProcessingApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getDataProcessingWebSocketUrl, WebSocketManager } from '../utils/websocket';
import TemplateMatchProgress from '../components/TemplateMatchProgress';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// 语言选项配置
const languageOptions = [
  { value: 'english', label: '🇺🇸 American English (美式英语)', text: 'As shown in the picture.' },
  { value: 'british', label: '🇬🇧 British English (英式英语)', text: 'As shown in the picture.' },
  { value: 'german', label: '🇩🇪 Deutsch (德语)', text: 'Wie in der Abbildung gezeigt.' },
  { value: 'french', label: '🇫🇷 Français (法语)', text: 'Comme le montre la figure.' },
  { value: 'spanish', label: '🇪🇸 Español (西班牙语)', text: 'Como se muestra en la figura.' },
  { value: 'italian', label: '🇮🇹 Italiano (意大利语)', text: 'Come mostrato nella figura.' },
  { value: 'polish', label: '🇵🇱 Polski (波兰语)', text: 'Jak pokazano na obrazku.' },
  { value: 'dutch', label: '🇳🇱 Nederlands (荷兰语)', text: 'Zoals afgebeeld op de afbeelding.' }
];

const DataProcessing = () => {
  const { user } = useAuth();
  const [fileList, setFileList] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [data, setData] = useState(null);
  const [hasProcessedData, setHasProcessedData] = useState(false);
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState(0);
  
  // WebSocket相关状态
  const [wsConnected, setWsConnected] = useState(false);
  const [realTimeProgress, setRealTimeProgress] = useState(null);
  const [processingLogs, setProcessingLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const wsManagerRef = useRef(null);
  const pollingRef = useRef(null);

  // 模板管理相关状态
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [matchedResult, setMatchedResult] = useState(null);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [matching, setMatching] = useState(false);
  
  // 异步模板匹配状态
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [showTemplateProgress, setShowTemplateProgress] = useState(false);
  
  // 监控状态变化
  useEffect(() => {
    console.log('showTemplateProgress状态变化:', showTemplateProgress);
  }, [showTemplateProgress]);
  
  useEffect(() => {
    console.log('currentTaskId状态变化:', currentTaskId);
    // 持久化任务ID到localStorage
    if (currentTaskId) {
      localStorage.setItem('currentTemplateTaskId', currentTaskId);
    } else {
      localStorage.removeItem('currentTemplateTaskId');
    }
  }, [currentTaskId]);
  
  // 模板匹配完成回调
  const handleTemplateMatchComplete = (resultId) => {
    console.log('模板匹配完成回调被调用:', resultId);
    console.log('设置matchedResult为:', resultId);
    setMatchedResult(resultId);
    setShowTemplateProgress(false);
    setMatching(false);
    addLog('success', `模板匹配完成！结果ID: ${resultId}`);
    message.success('模板匹配完成！');
  };
  
  // 模板匹配错误回调
  const handleTemplateMatchError = (errorMessage) => {
    console.log('模板匹配错误回调被调用:', errorMessage);
    setShowTemplateProgress(false);
    setMatching(false);
    addLog('error', `模板匹配失败: ${errorMessage}`);
    message.error(`模板匹配失败: ${errorMessage}`);
  };

  // 处理提示和历史记录
  const [processingTips, setProcessingTips] = useState(null);
  const [processingHistory, setProcessingHistory] = useState([]);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  // 处理模式选择
  const [processingMode, setProcessingMode] = useState('template_matching'); // 'data_processing' 或 'template_matching'
  
  // 语言选择
  const [selectedLanguage, setSelectedLanguage] = useState('english');

  // 连接WebSocket - 延迟连接确保用户信息完全加载
  useEffect(() => {
    if (user && user.id) {
      // 延迟500ms连接，确保用户信息完全加载
      const timer = setTimeout(() => {
        connectWebSocket();
      }, 500);
      
      return () => {
        clearTimeout(timer);
        if (wsManagerRef.current) {
          wsManagerRef.current.close();
        }
        stopPolling();
      };
    }
  }, [user]); // 依赖用户信息

  // 处理中始终轮询：多 worker 下 WebSocket 常收不到同进程推送
  useEffect(() => {
    if (processing) {
      startPolling();
      return () => stopPolling();
    }
    stopPolling();
  }, [processing]);

  // 获取当前模板和处理状态
  useEffect(() => {
    fetchCurrentTemplate();
    checkProcessingStatus();
    fetchProcessingTips();
    fetchProcessingHistory();
    
    // 刷新后恢复用户设置
    const savedProcessingMode = localStorage.getItem('dataProcessingMode');
    const savedLanguage = localStorage.getItem('dataProcessingLanguage');
    
    if (savedProcessingMode) {
      setProcessingMode(savedProcessingMode);
    }
    
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage);
    }
    
    // 恢复正在进行的模板匹配任务
    const savedTaskId = localStorage.getItem('currentTemplateTaskId');
    if (savedTaskId) {
      console.log('恢复模板匹配任务:', savedTaskId);
      setCurrentTaskId(savedTaskId);
      setShowTemplateProgress(true);
      addLog('info', `恢复模板匹配任务: ${savedTaskId.slice(0, 8)}...`);
    }
  }, []);

  // 检查是否有正在进行的处理
  const checkProcessingStatus = async () => {
    try {
      const status = await dataProcessingApi.getStatus();
      const processingStatus = status.processing_status || {};
      
      // 检查是否有正在进行的处理
      if (processingStatus.is_processing) {
        setProcessing(true);
        setProcessingMode('data_processing');
        localStorage.setItem('dataProcessingMode', 'data_processing');
        addLog('info', `检测到正在进行的处理: ${processingStatus.current_file || '未知文件'}`);
        if (processingStatus.start_time) {
          addLog('info', `处理开始时间: ${new Date(processingStatus.start_time).toLocaleString()}`);
        }
        
        // 设置当前进度
        setProgress(processingStatus.current_progress || 0);
        setRealTimeProgress({
          status: 'processing',
          message: processingStatus.current_step || `正在处理文件: ${processingStatus.current_file || ''}`,
          progress: processingStatus.current_progress || 0,
          processed: processingStatus.processed_rows || 0,
          total: processingStatus.total_rows || 0,
          failed: processingStatus.failed_rows || 0
        });
        
        addLog('warning', '处理正在进行中。可点击「停止处理」保留已完成行；也可离开本页，后台会继续跑。');
        return; // 如果正在处理，不需要检查已完成的数据
      }

      if (processingStatus.current_status === 'cancelled') {
        setProcessing(false);
        setStopping(false);
        addLog('warning', processingStatus.current_step || '上次处理已停止，可以开始新任务');
      }
      
      // 检查处理状态
      if (processingStatus.current_status === 'error') {
        addLog('error', `上次处理失败: ${processingStatus.error_message}`);
        setRealTimeProgress({
          status: 'error',
          message: processingStatus.error_message,
          progress: 0
        });
      }
      
      // 始终同步服务端最新 session，避免刷新后下载到旧任务
      const latestSessionId =
        status.session_id ||
        processingStatus.session_id ||
        null;
      if (latestSessionId) {
        setCurrentSessionId(latestSessionId);
      }

      // 如果有处理后的数据，恢复状态（放宽条件，只要有数据就启用下载/匹配）
      if (status.has_processed_data || (status.data_shape && status.data_shape[0] > 0)) {
        setHasProcessedData(true);
        // 先设置占位数据，确保下载按钮可用
        const placeholderData = [{ 
          状态: '处理完成', 
          说明: status.original_filename
            ? `最新结果: ${status.original_filename}`
            : '数据已处理完成，可以下载完整结果',
          总行数: status.data_shape ? status.data_shape[0] : 0,
          总列数: status.data_shape ? status.data_shape[1] : 0,
          最后处理时间: status.last_processed_time ? new Date(status.last_processed_time).toLocaleString() : '未知',
          处理状态: '大数据处理完成，支持直接下载'
        }];
        
        // 只放占位数据，避免在前端加载整表
        setData(placeholderData);
        addLog('success', '✅ 检测到处理完成的数据，下载功能已启用');
        addLog('info', `📊 数据规模: ${status.data_shape ? status.data_shape[0] : 0} 行 x ${status.data_shape ? status.data_shape[1] : 0} 列`);
        
        // 恢复处理结果数据 - 简化逻辑，直接尝试获取预览数据
        if (status.has_processed_data && status.data_shape && status.data_shape[0] > 0) {
          // 尝试获取实际数据用于显示（异步，不影响下载按钮）
          try {
            const dataResponse = await dataProcessingApi.getPreview();
            if (dataResponse && Array.isArray(dataResponse) && dataResponse.length > 0) {
              setData(dataResponse);
              addLog('success', `已恢复数据预览: ${dataResponse.length} 行`);
            } else {
              addLog('info', '预览数据为空，但可以下载完整结果');
            }
          } catch (previewError) {
            console.log('获取数据预览失败，但下载功能仍可用:', previewError);
            addLog('warning', '数据预览加载失败，但可以直接下载完整文件');
            // 保持占位数据，确保下载按钮可用
          }
        } else {
          // 即使没有数据形状信息，只要有处理完成标记，也显示完成状态
          addLog('info', '检测到处理完成标记，下载功能已启用');
        }
        
        setStatistics(status.statistics || {});
        setSteps(status.steps || []);
        
        // 显示恢复信息
        addLog('info', '检测到之前的处理结果，状态已恢复');
        
        // 对于大数据量处理，额外确保下载功能可用
        if (status.data_shape && status.data_shape[0] > 1000) {
          addLog('success', `🚀 大数据量处理完成 (${status.data_shape[0]} 行)，下载功能已强制启用`);
        }
        
        if (status.data_shape && status.data_shape[0] > 0) {
          addLog('success', `已恢复 ${status.data_shape[0]} 行 x ${status.data_shape[1]} 列的处理结果`);
          
          // 设置完成状态的进度信息
          setRealTimeProgress({
            status: 'completed',
            message: '数据处理已完成（从缓存恢复）',
            progress: 100,
            processed: status.statistics && status.statistics.processed_rows ? status.statistics.processed_rows : status.data_shape[0],
            total: status.statistics && status.statistics.total_rows ? status.statistics.total_rows : status.data_shape[0],
            failed: status.statistics && status.statistics.failed_rows ? status.statistics.failed_rows : 0
          });
          
          setProgress(100);
        }
        
        if (status.last_processed_time) {
          addLog('info', `最后处理时间: ${new Date(status.last_processed_time).toLocaleString()}`);
        }
      }
      
    } catch (error) {
      console.error('检查处理状态失败:', error);
      // 不显示错误消息，因为这只是状态检查
    }
  };

  const connectWebSocket = () => {
    // 清理现有连接
    if (wsManagerRef.current) {
      wsManagerRef.current.close();
    }
    
    // 检查用户信息 - 更详细的检查
    if (!user) {
      addLog('warning', '用户信息不完整，无法建立WebSocket连接');
      startPolling();
      return;
    }
    
    if (!user.id) {
      addLog('warning', '用户ID缺失，无法建立WebSocket连接');
      startPolling();
      return;
    }

    // 获取token - 检查多个可能的token位置
    const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
    if (!token) {
      addLog('error', '未找到认证token，无法建立WebSocket连接');
      startPolling();
      return;
    }

    // 获取WebSocket URL
    const wsUrl = getDataProcessingWebSocketUrl(user.id, token);
    if (!wsUrl) {
      addLog('error', '无法获取WebSocket连接地址');
      startPolling();
      return;
    }

    addLog('info', `正在连接WebSocket（用户ID: ${user.id}）`);

    // 创建WebSocket管理器
    wsManagerRef.current = new WebSocketManager(wsUrl, {
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      connectionTimeout: 5000
    });

    // 设置事件处理器
    wsManagerRef.current.onOpen = () => {
      setWsConnected(true);
      addLog('success', `WebSocket连接已建立 (用户ID: ${user.id})`);
      // 不再因 WS 连通而停止轮询：多 worker 下进度主要靠 /status 拉磁盘状态
    };

    wsManagerRef.current.onMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('解析WebSocket消息失败:', error);
      }
    };

    wsManagerRef.current.onClose = () => {
      setWsConnected(false);
      addLog('warning', 'WebSocket连接已断开');
    };

    wsManagerRef.current.onError = (error) => {
      console.error('WebSocket错误:', error);
      setWsConnected(false);
      addLog('warning', 'WebSocket连接失败，启用轮询模式');
      startPolling();
    };

    wsManagerRef.current.onReconnect = (attempt) => {
      addLog('info', `正在尝试重新连接... (${attempt}/5)`);
    };

    // 开始连接
    wsManagerRef.current.connect();
  };

  // 轮询方案：多 worker 下作为主进度来源（WebSocket 为增强）
  const startPolling = () => {
    if (pollingRef.current) return; // 避免重复轮询
    
    addLog('info', '启动进度轮询（兼容多进程）');
    let lastLoggedProgress = -1;
    
    pollingRef.current = setInterval(async () => {
      try {
        // 轮询获取处理状态
        const response = await dataProcessingApi.getStatus();
        if (response && response.processing_status) {
          const status = response.processing_status;
          
          // 如果有处理状态更新，显示进度
          if (status.is_processing) {
            setProcessing(true);
            setProcessingMode('data_processing');
            localStorage.setItem('dataProcessingMode', 'data_processing');
            if (status.session_id) {
              setCurrentSessionId(status.session_id);
            }
            setProgress(status.current_progress || 0);
            setRealTimeProgress({
              status: status.current_status || 'processing',
              message: status.current_step || '正在处理...',
              progress: status.current_progress,
              processed: status.processed_rows,
              total: status.total_rows,
              failed: status.failed_rows
            });
            const prog = Math.round(status.current_progress || 0);
            if (prog !== lastLoggedProgress && (prog % 5 === 0 || prog === 0)) {
              lastLoggedProgress = prog;
              addLog('info', `进度更新: ${status.current_step || prog + '%'}`);
            }
          } else if (status.current_status === 'completed') {
            setProgress(100);
            setRealTimeProgress(prev => ({
              ...(prev || {}),
              status: 'completed',
              message: '处理完成',
              progress: 100
            }));
            const latestSid = response.session_id || status.session_id;
            if (latestSid) {
              setCurrentSessionId(latestSid);
            }
            setHasProcessedData(true);
            addLog('success', '处理完成（通过轮询检测）');
            setProcessing(false);
            setStopping(false);
            stopPolling();
          } else if (status.current_status === 'error') {
            addLog('error', `处理失败: ${status.error_message || '未知错误'}`);
            setProcessing(false);
            setStopping(false);
            stopPolling();
          } else if (status.current_status === 'cancelled') {
            const latestSid = response.session_id || status.session_id;
            if (latestSid) {
              setCurrentSessionId(latestSid);
            }
            addLog('warning', '处理已停止，已保留已完成部分');
            setProcessing(false);
            setStopping(false);
            stopPolling();
          }
        }
      } catch (error) {
        console.error('轮询获取状态失败:', error);
        // 不显示错误，避免干扰用户
      }
    }, 1500);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      addLog('info', '停止轮询模式');
    }
  };

  const handleWebSocketMessage = (message) => {
    // 丢弃非本用户的进度，防止串台
    const msgUid = message?.user_id ?? message?.data?.user_id;
    if (msgUid != null && user?.id != null && Number(msgUid) !== Number(user.id)) {
      console.warn('忽略其他用户的WebSocket消息', msgUid, user.id);
      return;
    }
    switch (message.type) {
      case 'connection':
        addLog('success', message.message);
        break;
      case 'progress':
        handleProgressUpdate(message.data);
        break;
      case 'pong':
        // 心跳响应
        break;
      default:
        console.log('未知消息类型:', message);
    }
  };

  const handleProgressUpdate = (progressData) => {
    const { 
      status, 
      message: msg, 
      progress: prog, 
      processed, 
      total, 
      failed, 
      current_row,
      statistics: stats 
    } = progressData;
    
    // 更新进度
    if (prog !== undefined) {
      setProgress(prog);
    }
    
    // 添加日志
    addLog(status, msg);
    
    // 更新实时进度信息
    setRealTimeProgress({
      status,
      message: msg,
      progress: prog,
      processed,
      total,
      failed,
      current_row
    });

    // 更新统计信息
    if (stats) {
      setStatistics(stats);
    }
    
    // 刷新后 WebSocket 仍会推进度：恢复「处理中」以重新显示「停止处理」
    const terminal = status === 'completed' || status === 'error' || status === 'cancelled' || status === 'deleted';
    if (!terminal) {
      setProcessing(true);
      // 行级进度来自 AI 数据处理，确保模式正确以显示停止按钮
      if (processed != null || current_row != null || (prog != null && prog > 0 && prog < 100)) {
        setProcessingMode('data_processing');
        localStorage.setItem('dataProcessingMode', 'data_processing');
      }
    } else {
      setProcessing(false);
      setStopping(false);
      if (progressData.session_id) {
        setCurrentSessionId(progressData.session_id);
      }
      if (status === 'completed') {
        setProgress(100);
        setHasProcessedData(true);
        message.success('文件处理完成！');
        setData([{
          状态: '处理完成',
          说明: '数据已在后端缓存，可直接下载或匹配模板',
          最后处理时间: new Date().toLocaleString()
        }]);
      } else if (status === 'error') {
        message.error(msg);
      } else if (status === 'cancelled') {
        message.warning(msg || '处理已停止，已保留已完成部分');
        setHasProcessedData(true);
        setData([{
          状态: '已停止(部分完成)',
          说明: msg || '已保留已完成的 AI 处理结果，可下载或匹配模板',
          最后处理时间: new Date().toLocaleString()
        }]);
      } else if (status === 'deleted') {
        setRealTimeProgress(null);
        setProgress(0);
        setCurrentSessionId(null);
        message.success(msg || '任务已删除');
      }
    }
  };

  const addLog = (type, messageText) => {
    const log = {
      id: Date.now() + Math.random(),
      type,
      message: messageText,
      timestamp: new Date().toLocaleTimeString()
    };
    setProcessingLogs(prev => [...prev.slice(-49), log]); // 只保留最近50条日志
  };

  const handleStopProcessing = async () => {
    if (!processing || stopping) return;
    try {
      setStopping(true);
      addLog('warning', '正在请求停止处理...');
      const res = await dataProcessingApi.cancelProcessing();
      message.info(res.message || '已发送停止请求');
      addLog('warning', res.message || '已发送停止请求，将保留已完成的行');
      if (res.force_cleared) {
        setProcessing(false);
        setStopping(false);
        setRealTimeProgress({
          status: 'cancelled',
          message: res.message || '处理已停止，可以开始新的处理任务',
          progress: 0,
          processed: res.processed_rows || 0,
          total: res.total_rows || 0,
          failed: 0
        });
        addLog('success', '处理状态已释放，可以开始新任务');
        stopPolling();
      } else {
        setTimeout(async () => {
          try {
            const status = await dataProcessingApi.getStatus();
            const ps = status.processing_status || {};
            if (!ps.is_processing || ps.current_status === 'cancelled') {
              setProcessing(false);
              setStopping(false);
              stopPolling();
            }
          } catch (e) {
            setStopping(false);
          }
        }, 8000);
      }
    } catch (error) {
      setStopping(false);
      message.error('停止失败：' + (error.response?.data?.detail || error.message));
      addLog('error', '停止失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTask = async () => {
    if (deleting) return;
    try {
      setDeleting(true);
      addLog('warning', '正在删除处理任务...');
      const res = await dataProcessingApi.deleteProcessingTask();
      setProcessing(false);
      setStopping(false);
      setDeleting(false);
      setProgress(0);
      setRealTimeProgress(null);
      stopPolling();
      message.success(res.message || '任务已删除');
      addLog('success', res.message || '任务已删除，可以开始新的处理');
    } catch (error) {
      setDeleting(false);
      message.error('删除失败：' + (error.response?.data?.detail || error.message));
      addLog('error', '删除失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const showStopButton = processing || (
    realTimeProgress &&
    realTimeProgress.progress > 0 &&
    realTimeProgress.progress < 100 &&
    realTimeProgress.status !== 'completed' &&
    realTimeProgress.status !== 'error' &&
    realTimeProgress.status !== 'cancelled'
  );
  const showDeleteButton = showStopButton || realTimeProgress?.status === 'cancelled' || processing;

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('请先选择文件');
      return;
    }

    if (!wsConnected) {
      message.error('WebSocket未连接，无法显示实时进度');
      return;
    }

    // 以服务端状态为准，避免本地僵尸状态挡住新任务
    try {
      const serverStatus = await dataProcessingApi.getStatus();
      const ps = serverStatus.processing_status || {};
      if (ps.is_processing) {
        setProcessing(true);
        message.warning('已有处理正在进行中，请等待完成或点击停止处理');
        return;
      }
    } catch (statusError) {
      console.warn('检查服务端处理状态失败:', statusError);
    }

    if (processing) {
      message.warning('已有处理正在进行中，请等待完成');
      return;
    }

    try {
      setProcessing(true);
      setSteps([]);
      setProgress(0);
      setData(null);
      setProcessingLogs([]);
      setStatistics(null);
      // 开始新任务时清空旧 session，避免未刷新时下载到上一份结果
      setCurrentSessionId(null);
      setHasProcessedData(false);
      setMatchedResult(null);
      setRealTimeProgress({
        status: 'processing',
        message: '正在上传并开始处理...',
        progress: 0,
        processed: 0,
        total: 0,
        failed: 0
      });
      startPolling();

      addLog('info', '开始处理新文件...');
      addLog('info', '正在上传文件，请耐心等待...');
      addLog('info', '大文件处理可能需要几分钟时间，请不要关闭页面');

      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);

      let response;
      if (processingMode === 'template_matching') {
        // 模板匹配模式：不进行数据处理，保持原始格式
        addLog('info', '使用模板匹配模式，保持原始数据格式...');
        response = await dataProcessingApi.importExcelForTemplateMatching(fileList[0].originFileObj);
      } else {
        // 数据处理模式：进行完整的数据处理
        addLog('info', '使用数据处理模式，将进行数据增强...');
        addLog('info', `选择语言: ${languageOptions.find(lang => lang.value === selectedLanguage)?.label || 'American English'}`);
        response = await dataProcessingApi.importExcel(fileList[0].originFileObj, selectedLanguage);
      }

      console.log('处理响应:', response);
      const result = response.data || response;
      setHasProcessedData(true);
      setData([{
        状态: result.cancelled ? '已停止(部分完成)' : '处理完成',
        说明: result.cancelled
          ? (result.message || '已保留已完成的 AI 处理结果，可下载或匹配模板')
          : '数据已在后端缓存，可直接下载或匹配模板',
        最后处理时间: new Date().toLocaleString()
      }]);
      setSteps(result.steps || []);
      if (result.statistics) {
        setStatistics(result.statistics);
      }
      if (result.session_id) {
        setCurrentSessionId(result.session_id);
      }
      if (result.cancelled) {
        addLog('warning', result.message || '处理已停止，已保留已完成部分');
        message.warning(result.message || '处理已停止，已保留已完成部分');
      } else {
        addLog('success', '处理完成！数据已在后端缓存');
      }
      
      // 刷新处理历史记录
      fetchProcessingHistory();
    } catch (error) {
      message.error('文件处理失败：' + (error.response?.data?.detail || error.message));
      addLog('error', '文件处理失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
      setStopping(false);
    }
  };

  const handleDownload = async () => {
    try {
      // 下载前向服务端确认「最新」session，避免页面未刷新仍用旧 session_id
      let sessionToDownload = currentSessionId;
      try {
        const status = await dataProcessingApi.getStatus();
        const latestSid =
          status.session_id ||
          status.processing_status?.session_id ||
          null;
        if (latestSid) {
          sessionToDownload = latestSid;
          if (latestSid !== currentSessionId) {
            setCurrentSessionId(latestSid);
            addLog('info', `已切换到最新处理结果: ${status.original_filename || latestSid.slice(0, 8)}`);
          }
        }
      } catch (e) {
        console.warn('获取最新session失败，使用本地session:', e);
      }

      if (sessionToDownload) {
        await handleDownloadProcessedData(sessionToDownload);
        return;
      }
      
      // 否则使用旧的导出API作为降级（后端已按 DB 最新结果导出）
      const response = await dataProcessingApi.exportExcel();
      
      // 获取文件名（从响应头或使用默认值）
      let filename = 'processed_data.xlsx';
      try {
        // 尝试多种方式获取content-disposition
        const contentDisposition = response.headers?.['content-disposition'] || 
                                  response.headers?.contentDisposition ||
                                  (response.headers && response.headers.get && response.headers.get('content-disposition'));
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      } catch (headerError) {
        console.log('获取文件名失败，使用默认文件名:', headerError);
      }
      
      // 添加时间戳避免文件名冲突
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const nameWithoutExt = filename.replace('.xlsx', '');
      const finalFilename = `${nameWithoutExt}_${timestamp}.xlsx`;
      
      // 创建更兼容的Blob
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', finalFilename);
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      message.success(`文件下载成功: ${finalFilename}`);
    } catch (error) {
      console.error('下载错误:', error);
      message.error('文件下载失败：' + (error.response?.data?.detail || error.message));
    }
  };

  // 模板管理相关函数
  const fetchCurrentTemplate = async () => {
    try {
      const response = await dataProcessingApi.getCurrentTemplate();
      const tpl = response?.data || response;
      setCurrentTemplate(tpl || null);
    } catch (error) {
      console.error('获取当前模板失败:', error);
    }
  };

  const handleUploadTemplate = async (file) => {
    try {
      setTemplateUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await dataProcessingApi.uploadTemplate(file);
      message.success('模板上传成功');
      
      // 刷新当前模板
      fetchCurrentTemplate();
    } catch (error) {
      message.error('模板上传失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleMatchTemplate = async () => {
    if (!currentTemplate) {
      message.error('请先上传模板');
      return;
    }
    if (!data && !currentSessionId && !hasProcessedData) {
      message.error('请先处理数据');
      return;
    }
    try {
      setMatching(true);

      // 前端 data 常为「处理完成」状态摘要，不是产品行；交给后端从缓存/数据库取真实数据
      const statusFields = new Set(['状态', '说明', '总行数', '总列数', '最后处理时间', '处理状态', '数据行数', '数据列数']);
      const looksLikeStatus =
        Array.isArray(data) &&
        data.length > 0 &&
        typeof data[0] === 'object' &&
        Object.keys(data[0]).every((k) => statusFields.has(k));
      const payloadData = looksLikeStatus ? null : data;

      const response = await dataProcessingApi.matchTemplate(
        currentTemplate.template_id,
        payloadData,
        currentSessionId
      );
      
      console.log('模板匹配响应:', response); // 调试日志
      console.log('响应状态码:', response.status);
      console.log('响应数据:', response.data);
      
      // 获取任务ID和结果ID
      const taskId = response.data?.task_id || response.task_id;
      const resultId = response.data?.result_id || response.result_id;
      
      console.log('提取的任务ID:', taskId);
      console.log('提取的结果ID:', resultId);
      
      if (taskId) {
        console.log('设置任务ID:', taskId);
        console.log('显示进度组件');
        setCurrentTaskId(taskId);
        setShowTemplateProgress(true);
        setMatching(false); // 重置匹配状态
        addLog('info', `模板匹配已开始，任务ID: ${taskId.slice(0, 8)}...`);
        message.success('模板匹配已开始，请查看进度');
      } else if (resultId) {
        // 兼容旧版本：直接完成
        setMatchedResult(resultId);
        message.success('模板匹配成功');
      } else {
        console.error('响应中没有找到task_id或result_id:', response);
        message.error('模板匹配响应格式错误');
      }
    } catch (error) {
      // 详细提示
      let detail = error.response?.data?.detail || error.message;
      if (error.code === 'ECONNABORTED') {
        detail = '请求超时，请稍后重试或联系管理员。';
      }
      message.error('模板匹配失败：' + detail);
      console.error('模板匹配失败', error);
    } finally {
      setMatching(false);
    }
  };

  const handleDownloadMatched = async () => {
    if (!matchedResult) {
      message.error('没有可下载的匹配结果');
      return;
    }
    console.log('下载匹配结果，使用ID:', matchedResult);
    try {
      const response = await dataProcessingApi.downloadMatchedTemplate(matchedResult);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      // 动态获取后端返回的文件名
      let filename = 'matched_template.xlsm';
      const disposition = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition');
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('匹配结果下载成功');
    } catch (error) {
      message.error('下载失败：' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await dataProcessingApi.deleteCurrentTemplate();
      message.success('模板删除成功');
      setCurrentTemplate(null);
    } catch (error) {
      message.error('模板删除失败：' + (error.response?.data?.detail || error.message));
    }
  };

  const getColumns = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]).map(key => ({
      title: key,
      dataIndex: key,
      key: key,
      width: 150,
      ellipsis: true,
      render: (text) => {
        if (typeof text === 'object' && text !== null) {
          return (
            <Tooltip placement="topLeft" title={JSON.stringify(text)}>
              <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {JSON.stringify(text)}
              </div>
            </Tooltip>
          );
        }
        return (
          <Tooltip placement="topLeft" title={text}>
            <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </div>
          </Tooltip>
        );
      }
    }));
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'error':
        return 'red';
      case 'processing':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'processing':
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      default:
        return null;
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success':
        return '#52c41a';
      case 'error':
        return '#ff4d4f';
      case 'warning':
        return '#faad14';
      case 'info':
        return '#1890ff';
      default:
        return '#666';
    }
  };

  // 获取处理提示信息
  const fetchProcessingTips = async () => {
    try {
      const response = await dataProcessingApi.getProcessingTips();
      setProcessingTips(response);
    } catch (error) {
      console.error('获取处理提示失败:', error);
    }
  };

  // 获取处理历史记录
  const fetchProcessingHistory = async () => {
    try {
      const response = await dataProcessingApi.getHistory();
      setProcessingHistory(response || []);
    } catch (error) {
      console.error('获取处理历史失败:', error);
    }
  };

  // 下载处理结果
  const handleDownloadProcessedData = async (sessionId) => {
    try {
      const response = await dataProcessingApi.downloadProcessedData(sessionId);
      
      // 创建下载链接
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `processed_data_${sessionId}.xlsx`);
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      message.success('文件下载成功');
    } catch (error) {
      message.error('下载失败：' + (error.response?.data?.detail || error.message));
    }
  };

  // 下载模板匹配结果
  const handleDownloadTemplateResult = async (resultId) => {
    try {
      const response = await dataProcessingApi.downloadTemplateResult(resultId);
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_result_${resultId}.xlsx`);
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      message.success('模板结果下载成功');
    } catch (error) {
      message.error('下载失败：' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="p-6">
      <Card title="数据处理" className="mb-6">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          {/* 数据处理模式选择 */}
          <Card title="数据处理模式" size="small" style={{ marginBottom: '16px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>请选择数据处理模式：</Text>
              </div>
              <Space>
                <Button 
                  type={processingMode === 'data_processing' ? 'primary' : 'default'}
                  onClick={() => {
                    setProcessingMode('data_processing');
                    localStorage.setItem('dataProcessingMode', 'data_processing');
                  }}
                >
                  数据处理模式
                </Button>
                <Button 
                  type={processingMode === 'template_matching' ? 'primary' : 'default'}
                  onClick={() => {
                    setProcessingMode('template_matching');
                    localStorage.setItem('dataProcessingMode', 'template_matching');
                  }}
                >
                  模板匹配模式
                </Button>
              </Space>
              
              {/* 语言选择器 - 只在数据处理模式下显示 */}
              {processingMode === 'data_processing' && (
                <div style={{ marginTop: '12px' }}>
                  <Space align="center">
                    <GlobalOutlined style={{ color: '#1890ff' }} />
                    <Text strong>AI输出语言：</Text>
                    <Select
                      value={selectedLanguage}
                      onChange={(value) => {
                        setSelectedLanguage(value);
                        localStorage.setItem('dataProcessingLanguage', value);
                      }}
                      style={{ width: 200 }}
                      placeholder="选择语言"
                    >
                      {languageOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                    <Tooltip title={languageOptions.find(lang => lang.value === selectedLanguage)?.text}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        示例: {languageOptions.find(lang => lang.value === selectedLanguage)?.text}
                      </Text>
                    </Tooltip>
                  </Space>
                </div>
              )}
              
              <div>
                {processingMode === 'data_processing' ? (
                  <Text type="warning">
                    ⚠️ 数据处理模式：会添加EAN、SKU等字段，进行数据增强处理
                    {selectedLanguage !== 'english' && (
                      <span style={{ color: '#1890ff' }}>
                        ，AI将使用 {languageOptions.find(lang => lang.value === selectedLanguage)?.label} 输出
                      </span>
                    )}
                  </Text>
                ) : (
                  <Text type="success">
                    ✅ 模板匹配模式：保持原始数据格式，只进行模板匹配，适合直接上传到Amazon
                  </Text>
                )}
              </div>
            </Space>
          </Card>

          {/* 处理提示信息 */}
          {processingTips && (
            <Alert
              message="数据处理说明"
              description={
                <div>
                  <Paragraph style={{ marginBottom: '8px' }}>
                    <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <strong>处理时间：</strong>
                    {processingTips.estimated_times?.small_data} | 
                    {processingTips.estimated_times?.medium_data} | 
                    {processingTips.estimated_times?.large_data}
                  </Paragraph>
                  <Space wrap>
                    {processingTips.features?.map((feature, index) => (
                      <Tag key={index} color="blue" icon={<CheckCircleOutlined />}>
                        {feature}
                      </Tag>
                    ))}
                  </Space>
                  <div style={{ marginTop: '8px' }}>
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<InfoCircleOutlined />}
                      onClick={() => setShowTipsModal(true)}
                    >
                      查看详细说明
                    </Button>
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {/* WebSocket连接状态 */}
          <Alert
            message={
              <Space>
                <span>WebSocket连接状态:</span>
                <Tag color={wsConnected ? 'green' : 'red'}>
                  {wsConnected ? '已连接' : '未连接'}
                </Tag>
                {!wsConnected && (
                  <Button size="small" onClick={connectWebSocket}>
                    重新连接
                  </Button>
                )}
              </Space>
            }
            type={wsConnected ? 'success' : 'warning'}
            showIcon
          />
          
          {/* 文件上传区域 */}
          <Card title="文件上传" size="small">
            <Space>
              <Upload
                beforeUpload={() => false}
                fileList={fileList}
                onChange={({ fileList }) => setFileList(fileList)}
                maxCount={1}
                accept=".xlsx,.xlsm"
              >
                <Button icon={<UploadOutlined />}>选择Excel文件</Button>
              </Upload>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={processing && !stopping}
                icon={<ReloadOutlined />}
                disabled={!wsConnected || fileList.length === 0 || processing}
              >
                {processing ? '处理中...' : '开始处理'}
              </Button>
              {/* 处理中始终显示停止；暂停后可删除任务 */}
              {showStopButton ? (
                <Popconfirm
                  title="确定停止处理？"
                  description="已 AI 完成的行会保留，未处理的行保留原始数据。"
                  onConfirm={handleStopProcessing}
                  okText="停止"
                  cancelText="继续处理"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<StopOutlined />}
                    loading={stopping}
                    disabled={deleting}
                  >
                    {stopping ? '正在停止...' : '停止处理'}
                  </Button>
                </Popconfirm>
              ) : processingMode === 'data_processing' ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    提示：开始处理后会出现「停止处理」按钮（刷新后也会自动恢复）
                  </Text>
              ) : null}
              {showDeleteButton && (
                <Popconfirm
                  title="确定删除此任务？"
                  description="将清除进度显示并释放处理锁；已处理的数据仍可下载。"
                  onConfirm={handleDeleteTask}
                  okText="删除"
                  cancelText="取消"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleting}
                    disabled={stopping}
                  >
                    {deleting ? '正在删除...' : '删除任务'}
                  </Button>
                </Popconfirm>
              )}
              <Button
                type="primary"
                onClick={handleDownload}
                icon={<DownloadOutlined />}
                disabled={!data && !hasProcessedData && !currentSessionId}
              >
                下载处理结果
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const response = await dataProcessingApi.getStatus();
                    console.log('状态检查:', response);
                    if (response.has_processed_data || (response.data_shape && response.data_shape[0] > 0)) {
                      message.success('✅ 检测到处理后的数据，下载功能已启用');
                      // 强制启用下载按钮
                      setData([{ 
                        状态: '强制启用', 
                        说明: '手动启用下载功能',
                        数据行数: response.data_shape ? response.data_shape[0] : 0,
                        数据列数: response.data_shape ? response.data_shape[1] : 0,
                        检测时间: new Date().toLocaleString()
                      }]);
                      addLog('success', '🔧 手动检查：下载功能已强制启用');
                    } else {
                      message.warning('没有检测到处理后的数据');
                      addLog('warning', '手动检查：未检测到处理完成的数据');
                    }
                  } catch (error) {
                    message.error('状态检查失败: ' + error.message);
                    addLog('error', '手动检查失败: ' + error.message);
                  }
                }}
                size="small"
              >
                检查状态
              </Button>
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    // 直接尝试下载，不检查前端状态
                    addLog('info', '🚀 尝试强制下载...');
                    await handleDownload();
                    addLog('success', '✅ 强制下载成功');
                  } catch (error) {
                    if (error.response?.status === 404) {
                      message.error('后端没有可下载的数据，请先处理文件');
                      addLog('error', '强制下载失败：后端没有可下载的数据');
                    } else {
                      message.error('强制下载失败: ' + (error.response?.data?.detail || error.message));
                      addLog('error', '强制下载失败: ' + (error.response?.data?.detail || error.message));
                    }
                  }
                }}
                size="small"
                icon={<DownloadOutlined />}
                style={{ backgroundColor: '#ff7875', borderColor: '#ff7875' }}
              >
                强制下载
              </Button>
            </Space>
            {fileList.length > 0 && (
              <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                已选择文件: {fileList[0].name}
              </Paragraph>
            )}
          </Card>

          {/* 实时进度显示 */}
          {realTimeProgress && realTimeProgress.status !== 'completed' && (
            <Card title="实时处理进度" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Progress 
                  percent={Math.round(realTimeProgress.progress || 0)} 
                  status={realTimeProgress.status === 'error' ? 'exception' : 'active'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <Space>
                  {getStatusIcon(realTimeProgress.status)}
                  <Text strong>{realTimeProgress.message}</Text>
                </Space>
                {realTimeProgress.processed !== undefined && (
                  <Space>
                    <Text type="secondary">
                      已处理: {realTimeProgress.processed}/{realTimeProgress.total} 行
                    </Text>
                    {realTimeProgress.failed > 0 && (
                      <Text type="danger">失败: {realTimeProgress.failed} 行</Text>
                    )}
                  </Space>
                )}
              </Space>
            </Card>
          )}

          {/* 处理完成状态 */}
          {realTimeProgress && (realTimeProgress.status === 'completed' || realTimeProgress.status === 'error') && (
            <Card title="处理状态" size="small">
              <Space>
                {realTimeProgress.status === 'completed' ? (
                  <>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    <Text strong style={{ color: '#52c41a' }}>处理完成！</Text>
                    <Text type="secondary">文件已成功处理，可以下载结果</Text>
                  </>
                ) : (
                  <>
                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                    <Text strong style={{ color: '#ff4d4f' }}>处理失败</Text>
                    <Text type="secondary">{realTimeProgress.message}</Text>
                  </>
                )}
              </Space>
            </Card>
          )}

          {/* 处理统计信息 */}
          {statistics && (
            <Card title="处理统计" size="small">
              <Space wrap>
                <Tag color="blue">总行数: {statistics.total_rows}</Tag>
                <Tag color="green">成功: {statistics.processed_rows}</Tag>
                {statistics.failed_rows > 0 && (
                  <Tag color="red">失败: {statistics.failed_rows}</Tag>
                )}
                <Tag color="orange">处理时间: {statistics.processing_time?.toFixed(2)}秒</Tag>
                <Tag color="purple">成功率: {statistics.success_rate?.toFixed(1)}%</Tag>
              </Space>
            </Card>
          )}

          {/* 处理日志 */}
          {processingLogs && processingLogs.length > 0 && (
            <Card title="处理日志" size="small">
              <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                {processingLogs.map(log => (
                  <div key={log.id} style={{ marginBottom: '4px', fontSize: '12px' }}>
                    <Text type="secondary">[{log.timestamp}]</Text>
                    <Text style={{ color: getLogColor(log.type), marginLeft: '8px' }}>
                      {log.message}
                    </Text>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* 模板管理区域 */}
          <Card title="模板管理" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Upload
                  beforeUpload={(file) => {
                    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xlsm');
                    if (!isExcel) {
                      message.error('只允许上传 .xlsx 或 .xlsm 格式的Excel模板文件！');
                      return Upload.LIST_IGNORE;
                    }
                    handleUploadTemplate(file);
                    return false;
                  }}
                  accept=".xlsx,.xlsm"
                  showUploadList={false}
                  disabled={templateUploading}
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={templateUploading}
                  >
                    {currentTemplate ? '更换模板' : '上传模板'}
                  </Button>
                </Upload>
                
                <Button
                  type="primary"
                  onClick={handleMatchTemplate}
                  loading={matching}
                  disabled={!currentTemplate || (!data && !hasProcessedData && !currentSessionId)}
                >
                  匹配数据到模板
                </Button>
                
                <Button
                  type="primary"
                  onClick={handleDownloadMatched}
                  disabled={!matchedResult}
                  icon={<DownloadOutlined />}
                >
                  下载匹配结果
                </Button>
              </Space>
              
              
              {/* 模板匹配进度显示 */}
              {showTemplateProgress && currentTaskId && (
                <div style={{ marginTop: '16px' }}>
                  <TemplateMatchProgress
                    taskId={currentTaskId}
                    onComplete={handleTemplateMatchComplete}
                    onError={handleTemplateMatchError}
                    onDownload={handleDownloadMatched}
                  />
                </div>
              )}
              
              {matchedResult && (
                <Alert
                  message="模板匹配完成"
                  description={`结果ID: ${matchedResult}`}
                  type="success"
                  showIcon
                />
              )}
              
              {/* 当前模板信息 */}
              {currentTemplate && (
                <div>
                  <Divider orientation="left">当前模板</Divider>
                  <Card 
                    size="small"
                    style={{ marginBottom: 8 }}
                    extra={
                      <Popconfirm
                        title="确定要删除当前模板吗？"
                        onConfirm={handleDeleteTemplate}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button 
                          type="text" 
                          danger 
                          icon={<DeleteOutlined />}
                          size="small"
                        >
                          删除模板
                        </Button>
                      </Popconfirm>
                    }
                  >
                    <Space>
                      <Text strong>{currentTemplate.filename}</Text>
                      <Text type="secondary">
                        上传时间: {new Date(currentTemplate.upload_time).toLocaleString()}
                      </Text>
                      {currentTemplate.file_size && (
                        <Text type="secondary">
                          大小: {(currentTemplate.file_size / 1024).toFixed(1)}KB
                        </Text>
                      )}
                    </Space>
                  </Card>
                </div>
              )}
            </Space>
          </Card>

          {/* 处理结果表格 */}
          {data && Array.isArray(data) && data.length > 0 && (
            <Card 
              title="处理结果" 
              size="small"
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ 
                maxHeight: '500px', 
                overflow: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '4px'
              }}>
                <Table
                  columns={getColumns(data)}
                  dataSource={data}
                  rowKey={(record, index) => index}
                  scroll={{ x: 'max-content', y: 400 }}
                  size="small"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条数据`
                  }}
                />
              </div>
            </Card>
          )}

          {/* 处理步骤 - 移到底部并可折叠 */}
          {steps && steps.length > 0 && (
            <Card 
              title="处理步骤详情" 
              size="small"
              style={{ marginTop: '16px' }}
              bodyStyle={{ padding: '8px' }}
            >
              <details style={{ cursor: 'pointer' }}>
                <summary style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#fafafa', 
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  点击查看详细处理步骤 ({steps.length} 个步骤)
                </summary>
                <div style={{ 
                  maxHeight: '300px', 
                  overflow: 'auto',
                  padding: '8px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}>
                  <Timeline size="small">
                    {steps.map((step, index) => (
                      <Timeline.Item 
                        key={index}
                        color={getStepColor(step.status)}
                        dot={getStatusIcon(step.status)}
                      >
                        <div style={{ fontSize: '13px' }}>
                          <Text strong style={{ fontSize: '14px' }}>{step.name}</Text>
                          <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {step.description}
                            </Text>
                          </div>
                          <Space size="small" style={{ fontSize: '11px' }}>
                            {step.data_shape && (
                              <Tag size="small" color="blue">
                                {step.data_shape[0]} 行 x {step.data_shape[1]} 列
                              </Tag>
                            )}
                            {step.timestamp && (
                              <Tag size="small" color="default">
                                {new Date(step.timestamp).toLocaleTimeString()}
                              </Tag>
                            )}
                          </Space>
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              </details>
            </Card>
          )}
        </Space>
      </Card>

      {/* 处理历史记录 */}
      {processingHistory && processingHistory.length > 0 && (
        <Card title="处理历史记录" className="mb-6">
          <List
            dataSource={processingHistory}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button 
                    type="link" 
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadProcessedData(item.session_id)}
                    disabled={!item.total_rows || item.total_rows === 0}
                  >
                    下载
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />}
                  title={
                    <Space>
                      <span>{item.original_filename}</span>
                      <Badge 
                        status={item.total_rows > 0 ? 'success' : 'processing'} 
                        text={item.total_rows > 0 ? '已完成' : '处理中'} 
                      />
                    </Space>
                  }
                  description={
                    <div>
                      <div>处理时间: {new Date(item.created_at).toLocaleString()}</div>
                      <div>
                        <Space>
                          <Tag color="blue">总行数: {item.total_rows}</Tag>
                          <Tag color="green">成功: {item.processed_rows}</Tag>
                          {item.failed_rows > 0 && <Tag color="red">失败: {item.failed_rows}</Tag>}
                          <Tag color="orange">成功率: {item.success_rate?.toFixed(1)}%</Tag>
                        </Space>
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 详细说明模态框 */}
      <Modal
        title="数据处理详细说明"
        open={showTipsModal}
        onCancel={() => setShowTipsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowTipsModal(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {processingTips && (
          <div>
            <List
              dataSource={processingTips.tips}
              renderItem={(tip) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <div style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: tip.type === 'success' ? '#f6ffed' : 
                                        tip.type === 'warning' ? '#fff7e6' : '#e6f7ff',
                        color: tip.type === 'success' ? '#52c41a' : 
                               tip.type === 'warning' ? '#fa8c16' : '#1890ff'
                      }}>
                        {tip.type === 'success' ? <CheckCircleOutlined /> :
                         tip.type === 'warning' ? <ClockCircleOutlined /> : <InfoCircleOutlined />}
                      </div>
                    }
                    title={tip.title}
                    description={tip.content}
                  />
                </List.Item>
              )}
            />
            
            <Divider />
            
            <Title level={4}>预估处理时间</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div><Tag color="green">小数据量</Tag> {processingTips.estimated_times?.small_data}</div>
              <div><Tag color="orange">中等数据量</Tag> {processingTips.estimated_times?.medium_data}</div>
              <div><Tag color="red">大数据量</Tag> {processingTips.estimated_times?.large_data}</div>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DataProcessing;
